import React, { memo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Shell } from '../common/components/shell/Shell';
import { myPanalytRoute } from '../common/routes';
import SetupErrorComponent from '../common/v2/common/components/errors/pages/SetupError';
import { DEFAULT_LANDING_PAGE } from '../common/v2/constants';
import {
  useEffectiveRoleViewAuthorizationServiceContext,
  useExecutorRolesContext,
  useIntercomStoreContext,
  useLatestPreferencesContext,
} from '../common/v2/context/contexts';
import { ExecutorRoleContextProvider } from '../common/v2/context/ExecutorRoleContextProvider';
import { AuthenticationGuard } from '../common/v2/guards/AuthenticationGuard';
import { DomainGuard } from '../common/v2/guards/DomainGuard';
import { useRoutePersistence } from '../hooks/useRoutePersistence';
import { AdminRoutes } from './AdminRoutes';
import { CustomDashboardRoutes } from './CustomDashboardRoutes';
import { DashboardRoutes } from './DashboardRoutes';
import { DataUploadOnlyApp } from './DataUploadOnlyApp';
import { PanalytSuperAdminRoutes } from './PanalytSuperAdminRoutes';
import { IdleTimerGuard } from './pre-render-app-guards/IdleTimerGuard';
import { PrivateRoute } from './PrivateRoute';

const NormalApp = memo(() => {
  const latestPreferences = useLatestPreferencesContext();
  const companyLandingPage =
    latestPreferences.customLandingPage ?? DEFAULT_LANDING_PAGE;

  return (
    <Shell>
      <Switch>
        <Route path="/dashboard/custom">
          <CustomDashboardRoutes />
        </Route>
        <Route path="*">
          <Switch>
            <PrivateRoute
              key={myPanalytRoute.id}
              component={myPanalytRoute.comp()}
              {...myPanalytRoute}
            />
            ;
            <Route path="/dashboard">
              <DashboardRoutes />
            </Route>
            <Route path="/admin">
              <AdminRoutes />
            </Route>
            <Route path="/panalyt-superadmin">
              <PanalytSuperAdminRoutes />
            </Route>
            <Redirect from="*" to={companyLandingPage} />
          </Switch>
        </Route>
      </Switch>
    </Shell>
  );
});
NormalApp.displayName = 'NormalApp';

const Apps = () => {
  const executorRoles = useExecutorRolesContext();
  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();
  const isDataUploadOnlyRole = authorizationService.canUploadClientRawData();
  const latestPreferences = useLatestPreferencesContext();
  const companyLandingPage =
    latestPreferences.customLandingPage ?? DEFAULT_LANDING_PAGE;

  // Use the custom hook for route persistence
  useRoutePersistence(companyLandingPage);
  if (executorRoles.length === 0 && isDataUploadOnlyRole) {
    return <DataUploadOnlyApp />;
  } else if (executorRoles.length === 0) {
    return <SetupErrorComponent />;
  } else {
    return (
      <ExecutorRoleContextProvider>
        <NormalApp />
      </ExecutorRoleContextProvider>
    );
  }
};

export const PrivateRoutes = memo(() => {
  const intercomStore = useIntercomStoreContext();
  return (
    <AuthenticationGuard>
      <DomainGuard>
        <IdleTimerGuard>
          <Apps />
        </IdleTimerGuard>
      </DomainGuard>
    </AuthenticationGuard>
  );
});
PrivateRoutes.displayName = 'PrivateRoutes';
